import { ReactElement, useEffect, useState } from 'react';
import { BrowserRouter as Router, Switch, Route } from 'react-router-dom';
import { Container, Row, Col } from 'react-bootstrap';
import Operator from '../operator';
import Public from '../public/template';
import './App.scss';
import { IAppProps, UserType, UserData } from './types/IApp';
import { configureMap } from '../../config/AppConfig';
import { ToastContextProvider } from './contexts/ToastContext';
import ConfirmSubscriptionContainer from '../public/ConfirmSubscription/organisms/ConfirmSubscriptionContainer';
import {
  MsalAuthenticationTemplate,
  useAccount,
  useIsAuthenticated,
  useMsal,
} from '@azure/msal-react';
import { getData } from '../../api/ApiCommunication';
import Loader from '../ntisui/Atoms/Loader';
import { InteractionType } from '@azure/msal-browser';
import ErrorComponent from './ErrorComponent';
import LoadingComponent from './LoadingComponent';
import { registerTokenExpiryTimer } from '../../utils/tokenExpiry';

const App = (props: IAppProps): ReactElement => {
  const { accounts, instance } = useMsal();
  const accountDetails = useAccount(accounts[0] || {});
  const isAuthenticated = useIsAuthenticated();
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [mapApiScope, setApiScope] = useState<any>([]);

  async function fetchProfileData() {
    setIsLoading(true);
    await getData('userProfile', { Id: accountDetails?.localAccountId })
      .then((res) => {
        setUserData(res.data.data);
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  }

  useEffect(() => {
    configureMap().then((config: any) => {
      setApiScope([
        config.AAD_ADMIN_SCOPE,
        config.AAD_READER_SCOPE,
        config.AAD_GRAPH_API_SCOPE,
      ]);
    });
  }, []);

  useEffect(() => {
    if (accounts[0]?.idToken) {
      localStorage.setItem('userToken', accounts[0]?.idToken);
    }
    if (
      localStorage.getItem('userToken') &&
      props.userType === UserType.Public
    ) {
      const token = localStorage.getItem('userToken');
      if (token) {
        registerTokenExpiryTimer(token);
      }
    }
    // isAuthenticated && props.userType === UserType.Public && fetchProfileData();
  }, [isAuthenticated]);

  //this hook is used to get the refreshed token after expiry
  useEffect(() => {
    const callbackId = instance.addEventCallback(
      (event: { eventType: string; payload: { accessToken: any } }) => {
        if (event.eventType === 'acquireTokenSuccess') {
          // Handle the refreshed token here
          const refreshedToken = event.payload.accessToken;
          if (refreshedToken) {
            localStorage.setItem('userToken', refreshedToken);
            registerTokenExpiryTimer(refreshedToken);
          }
        }
      },
    );

    return () => {
      // Clean up the event callback when the component unmounts
      instance.removeEventCallback(callbackId);
    };
  }, [instance]);

  const authRequest = {
    scopes: ['User.Read'],
  };

  return (
    <ToastContextProvider>
      <Router>
        <Container fluid>
          <Row className="main-row">
            <Col className="px-0">
              {isLoading && isAuthenticated ? (
                <div>
                  <Loader />
                </div>
              ) : (
                <Switch>
                  <Route
                    exact
                    path="/"
                    render={() => {
                      return props.userType === UserType.Operator ? (
                        <MsalAuthenticationTemplate
                          interactionType={InteractionType.Redirect}
                          authenticationRequest={authRequest}
                          errorComponent={ErrorComponent}
                          loadingComponent={LoadingComponent}
                        >
                          <Operator />
                        </MsalAuthenticationTemplate>
                      ) : isAuthenticated &&
                        userData?.userRole === UserData.Subscriber &&
                        userData.status === UserData.NeedsVerification ? (
                        <ConfirmSubscriptionContainer />
                      ) : (
                        <Public />
                      );
                    }}
                  />
                  <Route
                    exact
                    path="/*"
                    component={() =>
                      isAuthenticated &&
                      userData?.userRole === UserData.Subscriber &&
                      userData.status === UserData.NeedsVerification ? (
                        <ConfirmSubscriptionContainer />
                      ) : (
                        <Public />
                      )
                    }
                  />
                </Switch>
              )}
            </Col>
          </Row>
        </Container>
      </Router>
    </ToastContextProvider>
  );
};

export default App;
